import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Unit } from '@/types';
import { UnitPreviewCard } from '@/components/unit/UnitPreviewCard';
import { STATUS_COLORS } from '../mapConfig';

// Helper to get status color safely
function getStatusColor(status: string): string {
  const statusKey = status as keyof typeof STATUS_COLORS;
  const colorConfig = STATUS_COLORS[statusKey];

  if (typeof colorConfig === 'object' && 'dark' in colorConfig) {
    return colorConfig.dark;
  }

  return STATUS_COLORS.default;
}

// Helper to generate semantic slug
const generateSlug = (unit: Unit) => {
  return unit.unit_code;
};

interface SmallClusterContentProps {
  units: Unit[];
  isMobile: boolean;
  initialActiveUnit?: Unit;
  onClose: () => void;
  onUnitClick?: (unit: Unit) => void;
  onUnitHover?: (unit: Unit | null) => void;
  navigate: (path: string) => void;
}

export function SmallClusterContent({
  units,
  isMobile,
  initialActiveUnit,
  onClose: _onClose,
  onUnitClick,
  onUnitHover,
  navigate
}: SmallClusterContentProps) {
  const [activeUnit, setActiveUnit] = useState(initialActiveUnit || units[0]);
  const [lastTap, setLastTap] = useState<{ unitId: string; time: number } | null>(null);
  const [collapsedCompanies, setCollapsedCompanies] = useState<Record<string, boolean>>({});

  // Group units by company
  const companyGroups = units.reduce((acc, unit) => {
    const companyName = unit.company?.name || 'Unknown';
    if (!acc[companyName]) {
      acc[companyName] = { units: [], logo: unit.company?.logo_url };
    }
    acc[companyName].units.push(unit);
    return acc;
  }, {} as Record<string, { units: Unit[]; logo?: string }>);

  const handleUnitTap = (unit: Unit) => {
    const now = Date.now();
    const DOUBLE_TAP_THRESHOLD = 300; // ms

    if (lastTap && lastTap.unitId === unit.id && now - lastTap.time < DOUBLE_TAP_THRESHOLD) {
      // Double tap detected - navigate using consistent slug format
      const slug = generateSlug(unit);
      // Navigate to detail view - URL state sync handles drawer state
      // Removing onClose() fixes race condition where drawer reopens during close animation
      navigate(`/map?unit=${slug}&view=detail`);
    } else {
      // Single tap - update preview
      setActiveUnit(unit);
      setLastTap({ unitId: unit.id, time: now });
      if (onUnitClick) {
        onUnitClick(unit);
      }
    }
  };

  const handleUnitHoverInternal = (unit: Unit) => {
    if (!isMobile) {
      // Update local state to change the preview card
      // NOTE: We intentionally do NOT call onUnitHover(unit) here because that
      // propagates to MapView, which then tries to show the unit's actual location
      // on the map, causing the cluster popup to jump. The cluster popup should
      // stay in place - we're just switching which unit's preview card is shown.
      setActiveUnit(unit);
    }
  };

  const handleAction = (action: 'view' | 'solar' | 'charger' | 'hvac' | 'starlink') => {
    const slug = generateSlug(activeUnit);
    const tabParam = action !== 'view' ? `&tab=${action}` : '';

    // CRITICAL FIX: Preserve cluster param to keep popup sticky during navigation
    // Get current cluster coordinates from URL to maintain sticky popup state
    // This prevents the popup from closing when clicking stat cards (Solar, HVAC, etc.)
    const searchParams = new URLSearchParams(window.location.search);
    const clusterParam = searchParams.get('cluster');
    const clusterSuffix = clusterParam ? `&cluster=${clusterParam}` : '';

    // Navigate to detail view - URL state sync handles drawer state
    // Removing onClose() fixes race condition where drawer reopens during close animation
    navigate(`/map?unit=${slug}&view=detail${tabParam}${clusterSuffix}`);
  };

  const toggleCompany = (companyName: string) => {
    setCollapsedCompanies(prev => ({
      ...prev,
      [companyName]: !prev[companyName]
    }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Active Unit Preview - Matches individual unit tooltip */}
      <div className="flex-shrink-0">
        <UnitPreviewCard
          unit={activeUnit}
          isMobile={isMobile}
          onAction={handleAction}
          imageHeight="h-[200px]"
          className="border-b border-border rounded-none shadow-none"
        />
      </div>

      {/* Companies List - Scrollable */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
        {Object.entries(companyGroups).map(([companyName, group], index) => {
          const isCollapsed = collapsedCompanies[companyName];

          return (
            <div key={companyName} className={index > 0 ? "border-t border-border" : ""}>
              {/* Company Header - Dropdown menu label style with green accent */}
              <div className="text-[10px] uppercase tracking-wider bg-muted/50 py-1 px-2 flex items-center gap-2">
                {group.logo && (
                  <div className="w-5 h-5 rounded overflow-hidden flex-shrink-0 bg-white">
                    <img
                      src={group.logo}
                      alt={companyName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <span className="text-[#8FB560] font-semibold flex-1">{companyName}</span>
                <span className="text-xs text-muted-foreground">({group.units.length})</span>
                <button
                  onClick={() => toggleCompany(companyName)}
                  className="w-4 h-4 flex items-center justify-center text-[#8FB560] hover:text-[#7fa050] transition-colors"
                  aria-label={isCollapsed ? "Expand company" : "Collapse company"}
                >
                  {isCollapsed ? (
                    <Plus className="w-3.5 h-3.5" />
                  ) : (
                    <Minus className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              {/* Unit Grid - 5 columns per row */}
              {!isCollapsed && (
                <div className="grid grid-cols-5 gap-x-1.5 gap-y-2 px-3 py-2">
              {group.units.slice(0, 10).map(unit => { // Limit to 10 per company (2 rows of 5)
                const isActive = activeUnit.id === unit.id;
                const statusColor = getStatusColor(unit.status);

                return (
                  <div
                    key={unit.id}
                    className={cn(
                      "flex flex-col items-center cursor-pointer group/unit transition-all duration-200",
                      isActive && "scale-105" // Scale up active item for better visibility
                    )}
                    onClick={() => handleUnitTap(unit)}
                    onMouseEnter={() => handleUnitHoverInternal(unit)}
                    onMouseLeave={() => {
                      // Reset hover state when mouse leaves (desktop only)
                      if (!isMobile && onUnitHover) {
                        onUnitHover(null);
                      }
                    }}
                  >
                    {/* Status-colored circle */}
                    <div className="relative mb-1">
                      {/* Active Ring - Enhanced visibility */}
                      {isActive && (
                        <div
                          className="absolute inset-0 -m-1 rounded-full transition-all animate-pulse border-[2px] border-[#8FB560] opacity-100"
                        />
                      )}

                      {/* Hover Ring - Shows on hover for non-active items */}
                      {!isActive && (
                        <div
                          className="absolute inset-0 -m-1 rounded-full transition-all opacity-0 group-hover/unit:opacity-60 border-[1px] border-[#8FB560]"
                        />
                      )}

                      <div
                        className={cn(
                          "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200",
                          "active:scale-95",
                          isActive && "shadow-lg shadow-[#8FB560]/30" // Add glow effect when active
                        )}
                        style={{ backgroundColor: statusColor }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M15 18H9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.578-.579l-1.5-4.5A1 1 0 0 0 16.382 6H14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="7" cy="18" r="2" stroke="white" strokeWidth="2"/>
                          <circle cx="17" cy="18" r="2" stroke="white" strokeWidth="2"/>
                        </svg>
                      </div>
                    </div>

                    {/* Unit Name/ID - Enhanced for active state, responsive font for long codes */}
                    <div className="flex flex-col items-center w-full">
                    <span className={cn(
                      "leading-tight font-medium text-center w-full whitespace-nowrap px-0.5 transition-all duration-200",
                      unit.unit_code.length > 8 ? "text-[clamp(7px,2vw,9px)]" : "text-[10px]",
                      isActive ? "text-[#8FB560] font-semibold" : "text-foreground/90"
                    )}>
                      {unit.unit_code}
                    </span>
                    </div>
                  </div>
                );
              })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tap hint */}
      {isMobile && (
      <p className="text-xs text-center text-muted-foreground py-1 border-t border-border/50 bg-muted/30">
          Tap to preview, double-tap to open
      </p>
      )}
    </div>
  );
}
